import { useRef, useState, useEffect, useContext } from "react";
import readNDJSONStream from "ndjson-readablestream";
import { throttle } from "lodash";
import { Spinner, ISpinnerStyles } from "@fluentui/react";
import { TypewriterEffect } from "../../components/UI/typewriter-effect";

import {
    chatApi,
    ChatRequest,
    generateHistory,
    HistoryRequest,
    AssistantsLoadingState,
    ChatResponse,
    Message,
    MessageContext,
    ErrorResponse,
    ChatHistoryLoadingState,
    CosmosDBStatus,
    getDocumentText,
    Conversation
} from "../../api";
import { Answer, AnswerError, AnswerLoading } from "../../components/Answer";
import { QuestionInput } from "../../components/QuestionInput";
import { ExampleList } from "../../components/Example";
import { UserChatMessage } from "../../components/UserChatMessage";
import { getToken } from "../../authConfig";
import { useMsal } from "@azure/msal-react";
import { AppStateContext } from "../../state/AppProvider";
import { ClearChatProvider } from "../../components/Sidebar/ClearChatContext";

import { EmailDialog } from "../../components/EmailDialog";
import { MobileSidebar } from "../../components/MobileSidebar";
import { DefaultSidebar } from "../../components/DefaultSidebar";
import { ChatHeader } from "../../components/ChatHeader";
import { ButtonTooltip } from "../../components/ButtonTooltip";
import { Disclaimer } from "../../components/Disclaimer";
import { LoginExpired } from "../../components/LoginExpired";
import { LoadingScreen } from "../../components/LoadingScreen";
import { Helmet } from "react-helmet-async";
import { ImageInput } from "../../api/models";

const Chat = () => {
    let userErrorMessage: string = `Something went wrong. If this issue persists, please send an email to both the addresses below:
    - DevTeam@equiomgroup.com
    - ITInfrastructure@equiomgroup.com`;

    const [iframeKey, setIframeKey] = useState(0);

    const [conversationId, setConversationId] = useState<string>();
    const lastQuestionRef = useRef<string>("");
    const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);
    const chatScrollRef = useRef<HTMLDivElement | null>(null);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isStreaming, setIsStreaming] = useState<boolean>(false);
    const [streamingToolCalls, setStreamingToolCalls] = useState<string[]>([]);
    const [error, setError] = useState<unknown>();

    const [activeCitation, setActiveCitation] = useState<string>();
    const [activeCitationTitle, setActiveCitationTitle] = useState<string>();
    const [showThoughts, setShowThoughts] = useState<boolean[]>([]);
    const [commentInputs, setCommentInputs] = useState<string[]>([]);
    const [showEmailDialog, setShowEmailDialog] = useState<boolean>(false);
    const [showDisclaimer, setShowDisclaimer] = useState<boolean>(true);
    const [isAutoScroll, setIsAutoScroll] = useState<boolean>(true);
    const [isGeneratingHistory, setIsGeneratingHistory] = useState<boolean>(false);
    const [activeVideos, setActiveVideos] = useState<string[]>([]);
    const [questionFiles, setQuestionFiles] = useState<{ files: string[] }[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [answers, setAnswers] = useState<[user: string, response: ChatResponse][]>([]);
    const [streamedAnswers, setstreamedAnswers] = useState<[user: string, response: ChatResponse][]>([]);

    const appStateContext = useContext(AppStateContext);
    const abortControllerRef = useRef<AbortController | null>(null);
    const latestPartialRef = useRef<string>("");
    const lastQuestionImagesRef = useRef<ImageInput[]>([]);
    const [questionImages, setQuestionImages] = useState<{ images: ImageInput[] }[]>([]);
    const lastQuestionFilesRef = useRef<string[]>([]);
    const MAX_FILES = 500;

    const welcomeText = [
        {
            text: "Ask ",
            className: "font-thin text-eq-green"
        },
        {
            text: appStateContext?.state.assistant?.name + " ",
            className: "font-thin text-eq-green"
        },
        {
            text: "a ",
            className: "font-thin"
        },
        {
            text: "question.",
            className: "font-thin"
        }
    ];

    const welcomeTextExamples = [
        {
            text: "Ask ",
            className: "font-thin text-eq-green"
        },
        {
            text: appStateContext?.state.assistant?.name + " ",
            className: "font-thin text-eq-green"
        },
        {
            text: "a ",
            className: "font-thin"
        },
        {
            text: "question, ",
            className: "font-thin"
        },
        {
            text: "or ",
            className: "font-thin"
        },
        {
            text: "select ",
            className: "font-thin"
        },
        {
            text: "an ",
            className: "font-thin"
        },
        {
            text: "example ",
            className: "font-thin"
        },
        {
            text: "below ",
            className: "font-thin"
        },
        {
            text: "to ",
            className: "font-thin"
        },
        {
            text: "get ",
            className: "font-thin"
        },
        {
            text: "started.",
            className: "font-thin"
        }
    ];

    const handleAsyncRequest = async (question: string, answers: [string, ChatResponse][], setAnswers: Function, responseBody: ReadableStream<any>) => {
        let answer: string = "";
        let askResponse: ChatResponse = {} as ChatResponse;
        let aborted = false;

        const updateState = (newContent: string) => {
            return new Promise(resolve => {
                setTimeout(() => {
                    answer += newContent;
                    latestPartialRef.current = answer; // update partial answer in case user stops query
                    const baseRole = askResponse?.choices?.[0]?.message?.role || "assistant";
                    const latestResponse: ChatResponse = {
                        ...askResponse,
                        choices: [{ ...askResponse.choices?.[0], message: { content: answer, role: baseRole } }]
                    };
                    setstreamedAnswers([...answers, [question, latestResponse]]);
                    resolve(null);
                }, 33);
            });
        };
        try {
            setIsStreaming(true);
            try {
                for await (const event of readNDJSONStream(responseBody)) {
                    if (
                        event["choices"] &&
                        event["choices"][0]["context"] &&
                        event["choices"][0]["context"]["metadata"] &&
                        event["choices"][0]["context"]["metadata"]["thoughts"]
                    ) {
                        event["choices"][0]["message"] = event["choices"][0]["delta"];
                        askResponse = event as ChatResponse;
                    } else if (event["choices"] && event["choices"][0]["delta"]?.["tool_call"]) {
                        const toolName: string = event["choices"][0]["delta"]["tool_call"]["name"];
                        setStreamingToolCalls(prev => [...prev, toolName]);
                    } else if (event["choices"] && event["choices"][0]["delta"]["content"]) {
                        setIsLoading(false);
                        await updateState(event["choices"][0]["delta"]["content"]);
                    } else if (event["choices"] && event["choices"][0]["context"]) {
                        // Update context with new keys from latest event
                        askResponse.choices[0].context = { ...askResponse.choices[0].context, ...event["choices"][0]["context"] };
                    } else if (event["error"]) {
                        throw Error(event["error"]);
                    }
                }
            } catch (e: any) {
                if (e.name === "AbortError") aborted = true;
                else throw e;
            }
        } finally {
            setIsStreaming(false);
        }
        const baseChoice = askResponse?.choices?.[0] as any;
        const role = baseChoice.message?.role || "assistant";
        const baseContext = (baseChoice?.context || {}) as any;
        const ctx = { ...baseContext, stopped: aborted, aborted_before_first_token: aborted && answer.length === 0 };

        const fullResponse: ChatResponse = {
            ...askResponse,
            choices: [{ ...askResponse.choices?.[0], message: { content: answer, role }, context: ctx }] as any
        };
        return fullResponse;
    };

    const client = useMsal().instance;

    const makeApiRequest = async (question: string, images?: ImageInput[]) => {
        lastQuestionRef.current = question;
        lastQuestionImagesRef.current = images || [];
        let newQuestionFiles: string[] = [];
        if (appStateContext?.state.files && appStateContext?.state.files.length > 0) {
            newQuestionFiles = appStateContext?.state.files
                .filter(fileItem => fileItem.new)
                .map(fileItem => {
                    return fileItem.file.name;
                });
        }
        lastQuestionFilesRef.current = newQuestionFiles;

        error && setError(undefined);
        setIsLoading(true);
        setStreamingToolCalls([]);
        setActiveCitation(undefined);
        setActiveCitationTitle(undefined);

        setShowThoughts(currentShowThoughts => [...currentShowThoughts, false]);
        setActiveVideos(currentActiveVideos => [...currentActiveVideos, ""]);
        setCommentInputs(currentCommentInputs => [...currentCommentInputs, ""]);
        setQuestionFiles(currentQuestionFiles => [...currentQuestionFiles, { files: newQuestionFiles }]);
        setQuestionImages(currentQuestionImages => [...currentQuestionImages, { images: images || [] }]);
        appStateContext?.dispatch({ type: "TOGGLE_ANSWER_LOADING" });
        appStateContext?.dispatch({ type: "UPDATE_ALL_FILES_NEW" });
        latestPartialRef.current = "";

        const token = client ? await getToken(client) : undefined;

        try {
            const messages: Message[] = answers.flatMap(a => [
                { content: a[0], role: "user" },
                { content: a[1].choices[0].message.content, role: "assistant" }
            ]);

            const previousToolCallHistory = answers.length ? (answers[answers.length - 1][1].choices[0].context?.metadata?.tool_call_history ?? []) : [];

            let metadata: Record<string, any> | undefined = undefined;
            if (
                (appStateContext?.state.files && appStateContext?.state.files.length > 0) ||
                (images && images.length > 0) ||
                previousToolCallHistory.length > 0
            ) {
                metadata = {
                    ...(previousToolCallHistory.length > 0 && { tool_call_history: previousToolCallHistory }),
                    ...(appStateContext?.state.files?.length && {
                        files: appStateContext.state.files.map(fileItem => ({
                            fileName: fileItem.file.name,
                            fileText: fileItem.text,
                            newFile: fileItem.new
                        }))
                    }),
                    ...(images?.length && {
                        images: images.map(img => ({
                            name: img.name,
                            dataUrl: img.dataUrl,
                            mimeType: img.mimeType,
                            size: img.size
                        }))
                    })
                };
            }

            let userContext: MessageContext = {
                metadata: metadata,
                assistant_id: appStateContext?.state.assistant?.id,
                assistant_prompt_message: appStateContext?.state.assistant?.system_prompt_message
            };

            const request: ChatRequest = {
                assistant: appStateContext?.state.assistant?.assistant || "",
                messages: [...messages, { content: question, role: "user" }],
                context: userContext,
                stream: true,
                session_state: answers.length ? answers[answers.length - 1][1].choices[0].session_state : null
            };

            abortControllerRef.current = new AbortController();
            const response = await chatApi(request, token?.accessToken, abortControllerRef.current.signal);
            if (!response.body) {
                throw Error("No response body");
            }
            const parsedResponse: ChatResponse = await handleAsyncRequest(question, answers, setAnswers, response.body);

            if (
                (parsedResponse.choices?.[0]?.message?.content ?? "") === "" &&
                !parsedResponse.choices?.[0]?.context?.stopped &&
                !parsedResponse.choices?.[0]?.context?.aborted_before_first_token
            ) {
                // don't error on user stop
                throw Error(userErrorMessage);
            }

            setAnswers((prevAnswers: [user: string, response: ChatResponse][]) => {
                const updatedAnswers: [user: string, response: ChatResponse][] = [...prevAnswers, [question, parsedResponse]];

                //all messages, new and old
                const messages: Message[] = updatedAnswers.flatMap(a => [
                    { content: a[0], role: "user" },
                    { content: a[1].choices[0].message.content, role: "assistant" }
                ]);

                const historyRequest: HistoryRequest = {
                    conversation_id: conversationId,
                    assistant: appStateContext?.state.assistant?.assistant || "",
                    assistant_id: appStateContext?.state.assistant?.id || "",
                    messages: [...messages],
                    context: parsedResponse.choices[0].context,
                    user_context: userContext
                };

                setIsGeneratingHistory(true);
                generateHistory(historyRequest, token?.accessToken)
                    .then(histResponse => {
                        if (!histResponse.ok) {
                            throw Error("Network response was not ok");
                        }
                        return histResponse.json();
                    })
                    .then(data => {
                        let resultConversation: Conversation | undefined;
                        if (!data.conversation) {
                            throw Error("Invalid response body");
                        }
                        if (!conversationId) {
                            resultConversation = {
                                id: data.conversation.id,
                                assistant: data.conversation.assistant,
                                assistant_id: data.conversation.assistant_id,
                                title: data.conversation.title,
                                messages: data.messages,
                                date: data.conversation.date
                            };
                            appStateContext?.dispatch({ type: "UPDATE_CURRENT_CHAT", payload: resultConversation });
                            setConversationId(data.conversation.conversation_id);
                        } else {
                            resultConversation = appStateContext?.state?.chatHistory?.find(conv => conv.id === conversationId);
                            if (!resultConversation) {
                                throw Error("Invalid conversation id");
                            }
                            resultConversation.messages.push(data.messages[0]);
                            resultConversation.messages.push(data.messages[1]);
                            resultConversation.date = data.conversation.date;
                            let updatedConversation = { ...resultConversation };
                            appStateContext?.dispatch({ type: "UPDATE_CURRENT_CHAT", payload: updatedConversation });
                        }
                        return resultConversation;
                    })
                    .then(resultConversation => {
                        if (resultConversation) {
                            appStateContext?.dispatch({ type: "UPDATE_CHAT_HISTORY", payload: resultConversation });
                        }
                    })
                    .catch(e => {
                        appStateContext?.dispatch({ type: "UPDATE_CHAT_HISTORY_LOADING_STATE", payload: ChatHistoryLoadingState.Fail });
                        appStateContext?.dispatch({ type: "SET_COSMOSDB_STATUS", payload: { cosmosDB: false, status: CosmosDBStatus.NotWorking } });
                        console.error(e);
                    })
                    .finally(() => {
                        setIsGeneratingHistory(false);
                    });

                return updatedAnswers;
            });
        } catch (e: any) {
            const isAbort = e?.name === "AbortError" || String(e?.message || "").includes("aborted");
            if (isAbort) {
                const partial = latestPartialRef.current;
                const role = "assistant";
                const parsedResponse: ChatResponse = {
                    choices: [{ message: { content: partial || "", role }, context: { stopped: true, aborted_before_first_token: !partial } }]
                } as unknown as ChatResponse;
                setAnswers((prevAnswers: [user: string, response: ChatResponse][]) => {
                    const updatedAnswers: [user: string, response: ChatResponse][] = [...prevAnswers, [question, parsedResponse]];
                    const messages: Message[] = updatedAnswers.flatMap(a => [
                        { content: a[0], role: "user" },
                        { content: a[1].choices[0].message.content, role: "assistant" }
                    ]);
                    const historyRequest: HistoryRequest = {
                        conversation_id: conversationId,
                        assistant: appStateContext?.state.assistant?.assistant || "",
                        assistant_id: appStateContext?.state.assistant?.id || "",
                        messages: [...messages],
                        context: parsedResponse.choices[0].context,
                        user_context: {
                            metadata: undefined,
                            assistant_id: appStateContext?.state.assistant?.id,
                            assistant_prompt_message: appStateContext?.state.assistant?.system_prompt_message
                        }
                    };
                    setIsGeneratingHistory(true);
                    generateHistory(historyRequest, token?.accessToken)
                        .catch(() => {})
                        .finally(() => {
                            setIsGeneratingHistory(false);
                        });
                    return updatedAnswers;
                });
            } else {
                setError(e);
            }
        } finally {
            setIsLoading(false);
            setIsStreaming(false);
            abortControllerRef.current = null;
            appStateContext?.dispatch({ type: "TOGGLE_ANSWER_LOADING" });
        }
    };

    const clearChat = () => {
        if (lastQuestionRef.current && !appStateContext?.state.answerLoading) {
            lastQuestionRef.current = "";
            lastQuestionFilesRef.current = [];
            lastQuestionImagesRef.current = [];
            error && setError(undefined);
            setActiveCitation(undefined);
            setActiveCitationTitle(undefined);
            setShowThoughts([]);
            setCommentInputs([]);
            setAnswers([]);
            setstreamedAnswers([]);
            setQuestionFiles([]);
            setQuestionImages([]);
            setConversationId(undefined);
            appStateContext?.dispatch({ type: "REMOVE_ALL_FILES" });
            if (fileInputRef && fileInputRef.current) {
                fileInputRef.current.value = "";
            }
            appStateContext?.dispatch({
                type: "UPDATE_ASSISTANT",
                payload: appStateContext?.state.assistant
            });
            appStateContext?.dispatch({ type: "UPDATE_CURRENT_CHAT", payload: null });
            if (windowWidth < 768 && appStateContext?.state.isChatHistoryOpen) {
                appStateContext?.dispatch({ type: "TOGGLE_CHAT_HISTORY" });
            }
        }
    };

    useEffect(() => {
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" });
    }, [isLoading]);

    useEffect(() => {
        if (isAutoScroll) {
            chatMessageStreamEnd.current?.scrollIntoView({ behavior: "auto" });
        }
    }, [streamedAnswers]);

    const scrollToEnd = () => {
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" });
    };

    const handleScroll = () => {
        const chatContainer = chatScrollRef.current;
        if (chatContainer) {
            const { scrollTop, scrollHeight, clientHeight } = chatContainer;
            const isScrolledToBottom = Math.ceil(scrollTop + clientHeight) + 1 >= scrollHeight;
            setIsAutoScroll(isScrolledToBottom);
        }
    };

    useEffect(() => {
        const chatContainer = chatScrollRef.current;
        if (chatContainer) {
            chatContainer.addEventListener("scroll", handleScroll);
        }
        return () => {
            if (chatContainer) {
                chatContainer.removeEventListener("scroll", handleScroll);
            }
        };
    }, [lastQuestionRef.current]);

    const onExampleClicked = (example: string) => {
        makeApiRequest(example);
    };

    const onShowCitation = (citation: string, title: string, index: number) => {
        if (windowWidth < 1280) {
            window.open(citation, "_blank");
        } else {
            setActiveCitation(citation);
            setActiveCitationTitle(title);
            setIframeKey(prevKey => prevKey + 1);
        }
    };

    const onCloseCitation = () => {
        setActiveCitation(undefined);
        setActiveCitationTitle(undefined);
    };

    const onOpenCitation = () => {
        window.open(activeCitation, "_blank");
    };

    const onThoughtProcessClicked = (index: number) => {
        if (appStateContext?.state.currentChat && showThoughts.length === 0) {
            const updatedShowThoughts: boolean[] = [];
            const messages = appStateContext.state.currentChat.messages;

            for (let i = 0; i < messages.length - 1; i++) {
                if (messages[i].message.role === "user" && messages[i + 1].message.role === "assistant") {
                    updatedShowThoughts.push(false);
                }
            }
            setShowThoughts(updatedShowThoughts);
        }

        setShowThoughts(currentShowThoughts => {
            const updatedShowThoughts = [...currentShowThoughts];
            updatedShowThoughts[index] = !updatedShowThoughts[index];
            return updatedShowThoughts;
        });
    };

    const onSetCommentInput = (comment: string, index: number) => {
        setCommentInputs(currentCommentInputs => {
            const updatedCommentInputs = [...currentCommentInputs];
            updatedCommentInputs[index] = comment;
            return updatedCommentInputs;
        });
    };

    const handleHistoryClick = () => {
        appStateContext?.state.isChatHistoryOpen
            ? document.documentElement.style.setProperty("--ch", `${0}px`)
            : document.documentElement.style.setProperty("--ch", `${300}px`);
        appStateContext?.dispatch({ type: "TOGGLE_CHAT_HISTORY" });
    };

    const [windowHeight, setWindowHeight] = useState(window.innerHeight);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    useEffect(() => {
        const vh = window.innerHeight;
        document.documentElement.style.setProperty("--vh", `${vh}px`);
        document.documentElement.style.setProperty("--ch", `${0}px`);
    }, []);

    useEffect(() => {
        // The function won't be called more often than every 200ms
        const handleResize = throttle(() => {
            const vh = window.innerHeight;
            const vw = window.innerWidth;
            setWindowHeight(vh);
            setWindowWidth(vw);
            document.documentElement.style.setProperty("--vh", `${vh}px`);
        }, 200);

        window.addEventListener("resize", handleResize);

        // Clean up the event listener on component unmount
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useEffect(() => {
        if (appStateContext?.state.currentChat && appStateContext?.state.currentChat.id !== conversationId) {
            if (conversationId) {
                appStateContext?.dispatch({ type: "REMOVE_ALL_FILES" });
                if (fileInputRef && fileInputRef.current) {
                    fileInputRef.current.value = "";
                }
            }
            error && setError(undefined);
            setActiveCitation(undefined);

            const updatedShowThoughts: boolean[] = [];
            const updatedCommentInputs: string[] = [];
            const transformedAnswers: [string, ChatResponse][] = [];
            const messages = appStateContext.state.currentChat.messages;
            const files: { files: string[] }[] = [];
            const imagesByQuestion: { images: ImageInput[] }[] = [];

            for (let i = 0; i < messages.length - 1; i++) {
                if (messages[i].message.role === "user" && messages[i + 1].message.role === "assistant") {
                    transformedAnswers.push([
                        messages[i].message.content as string,
                        {
                            choices: [
                                {
                                    index: i,
                                    message: messages[i + 1].message,
                                    context: messages[i + 1].context,
                                    session_state: null,
                                    message_id: messages[i + 1].id,
                                    feedback: messages[i + 1].feedback
                                }
                            ]
                        } as ChatResponse
                    ]);

                    let allFiles = messages[i].context.metadata?.files;
                    if (allFiles) {
                        let newFiles = allFiles.filter((fileItem: { fileName: string; fileText: string; newFile: string }) => fileItem.newFile);
                        if (newFiles) {
                            files.push({ files: newFiles.map((fileItem: { fileName: string }) => fileItem.fileName) });
                            newFiles.map((fileItem: { fileName: string; fileText: string }) =>
                                appStateContext?.dispatch({
                                    type: "ADD_CHAT_HISTORY_FILE",
                                    payload: { name: fileItem.fileName, text: fileItem.fileText }
                                })
                            );
                        } else {
                            files.push({ files: [] });
                        }
                    } else {
                        files.push({ files: [] });
                    }

                    const histImages = (messages[i].context.metadata?.images || []) as ImageInput[];
                    imagesByQuestion.push({ images: histImages });

                    updatedShowThoughts.push(false);
                    updatedCommentInputs.push("");
                }
            }
            if (messages[messages.length - 1].message.role === "user") {
                // Final assistant message is missing
                setError(userErrorMessage);
            }

            setShowThoughts(updatedShowThoughts);
            setCommentInputs(updatedCommentInputs);
            setAnswers(transformedAnswers);
            setstreamedAnswers(transformedAnswers);
            setQuestionFiles(files);
            setQuestionImages(imagesByQuestion);

            setConversationId(appStateContext.state.currentChat.id);

            if (appStateContext.state.assistants) {
                const currentAssistant = appStateContext.state.assistants?.find(assist =>
                    assist.assistant === "custom"
                        ? assist.id === appStateContext.state.currentChat?.assistant_id
                        : assist.assistant === appStateContext.state.currentChat?.assistant
                );
                appStateContext?.dispatch({ type: "UPDATE_ASSISTANT", payload: currentAssistant || null });
            }

            for (let i = messages.length - 1; i >= 0; i--) {
                if (messages[i].message.role === "user") {
                    lastQuestionRef.current = messages[i].message.content as string;
                    break;
                }
            }
        } else if (appStateContext?.state.currentChat) {
            const transformedAnswers: [string, ChatResponse][] = [];
            const messages = appStateContext.state.currentChat.messages;
            for (let i = 0; i < messages.length - 1; i++) {
                if (messages[i].message.role === "user" && messages[i + 1].message.role === "assistant") {
                    transformedAnswers.push([
                        messages[i].message.content as string,
                        {
                            choices: [
                                {
                                    index: i,
                                    message: messages[i + 1].message,
                                    context: messages[i + 1].context,
                                    session_state: null,
                                    message_id: messages[i + 1].id,
                                    feedback: messages[i + 1].feedback
                                }
                            ]
                        } as ChatResponse
                    ]);
                }
            }
            setAnswers(transformedAnswers);
            setstreamedAnswers(transformedAnswers);
        } else {
            clearChat;
        }
    }, [appStateContext?.state.currentChat]);

    function copyToClipboard(text: string) {
        if (navigator.clipboard) {
            navigator.clipboard
                .writeText(text)
                .then(() => {
                    console.log("Text copied to clipboard");
                })
                .catch(err => {
                    console.error("Failed to copy text using Clipboard API: ", err);
                    // Try the fallback method
                    fallbackCopyTextToClipboard(text);
                });
        } else {
            fallbackCopyTextToClipboard(text);
        }
    }

    function fallbackCopyTextToClipboard(text: string) {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
            // Using the Clipboard API again for the fallback method
            navigator.clipboard.writeText(text);
            console.log("Text copied to clipboard using fallback");
        } catch (err) {
            console.error("Fallback failed too: ", err);
        }
        document.body.removeChild(textArea);
    }

    const spinnerStyles: ISpinnerStyles = {
        circle: {
            borderColor: "#0062b8 #0062b8 #bee1ff00 #bee1ff00",
            height: "16px",
            width: "16px"
        }
    };

    const stopStreaming = () => {
        abortControllerRef.current?.abort();
        abortControllerRef.current = null;
    };

    const uploadFile = () => {
        const input = fileInputRef.current;
        if (!input || !input.files?.length || !appStateContext?.state.files) {
            return;
        } else {
            const filesArray = Array.from(input.files);
            if (appStateContext?.state.files.filter(fileItem => fileItem.new).length + filesArray.length > MAX_FILES) {
                alert(`You can only upload a maximum of ${MAX_FILES} files.`);
                return;
            }
            const allowedTypes = [
                "application/pdf",
                "application/msword",
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                "text/plain"
            ];
            const allAreAllowedTypes = filesArray.every(file => allowedTypes.includes(file.type));
            if (!allAreAllowedTypes) {
                alert("Only PDF, Word (.pdf, .doc, .docx), or plain text (.txt) documents are allowed.");
                return;
            }

            filesArray.map((file, index) => {
                if (file.size > 10485760) {
                    alert(`File ${file.name} exceeds the maximum size of 10MB.`);
                    return;
                }
                appStateContext?.dispatch({ type: "ADD_FILE", payload: file.name });
                const formData = new FormData();
                if (input.files) {
                    formData.append("file", input.files[index]);
                }
                getDocumentText(formData, appStateContext?.state.token?.accessToken)
                    .then(response => response.json())
                    .then(data => {
                        appStateContext?.dispatch({
                            type: "UPDATE_FILE_TEXT",
                            payload: { name: file.name, text: data.text }
                        });
                        appStateContext?.dispatch({
                            type: "UPDATE_FILE_STATUS",
                            payload: { name: file.name, status: "Success" }
                        });
                    })
                    .catch(error => {
                        console.error(error);
                        appStateContext?.dispatch({
                            type: "UPDATE_FILE_STATUS",
                            payload: { name: file.name, status: "Error" }
                        });
                    });
            });
        }
    };

    return (
        <>
            <ClearChatProvider clearChat={clearChat}>
                <Helmet>
                    <title>AskEllie | Chat</title>
                </Helmet>
                <div className="relative z-0 flex h-full w-full overflow-hidden bg-eq-light-grey">
                    {windowWidth >= 976 && (
                        <DefaultSidebar
                            onNewClick={clearChat}
                            newDisabled={!lastQuestionRef.current || appStateContext?.state.answerLoading}
                            onHistoryClick={handleHistoryClick}
                        />
                    )}
                    {appStateContext?.state.loginExpiredLoading ? (
                        <LoadingScreen open={true} />
                    ) : appStateContext?.state.loginExpired ? (
                        <LoginExpired open={true} />
                    ) : (
                        <Disclaimer open={showDisclaimer} setOpen={b => setShowDisclaimer(b)} />
                    )}
                    <EmailDialog open={showEmailDialog} setOpen={b => setShowEmailDialog(b)} messages={appStateContext?.state.currentChat?.messages || []} />
                    <div className="relative flex h-full max-w-full flex-1 flex-col overflow-hidden">
                        <ChatHeader
                            onNewClick={clearChat}
                            newDisabled={!lastQuestionRef.current || appStateContext?.state.answerLoading}
                            onHistoryClick={handleHistoryClick}
                            emptyChat={!lastQuestionRef.current}
                            windowWidth={windowWidth}
                        />
                        <main
                            className={
                                "relative h-full w-full flex-1 overflow-auto transition-width " +
                                (activeCitation ? "xl:flex " : "") +
                                (appStateContext?.state.isChatHistoryOpen ? "xl:pl-6" : "")
                            }
                        >
                            <div
                                role="presentation"
                                tabIndex={0}
                                className={"flex h-full flex-col focus-visible:outline-0 " + (activeCitation ? "xl:w-1/2" : "")}
                            >
                                <div className="flex-1 overflow-hidden">
                                    {appStateContext?.state.assistant &&
                                        (!lastQuestionRef.current && windowWidth >= 976 ? (
                                            <div className="relative h-full">
                                                <div className="flex flex-col gap-3 h-full w-full items-center justify-center text-[36px] lg:text-[50px] font-thin leading-[56px]">
                                                    <span className="max-w-4xl text-center">
                                                        {showDisclaimer == false && (
                                                            <TypewriterEffect
                                                                key={appStateContext.state.assistant?.name}
                                                                words={!!appStateContext?.state.assistant?.examples?.length ? welcomeTextExamples : welcomeText}
                                                                cursorClassName="text-eq-green font-thin"
                                                                cursorFinishedClassName="text-eq-light-grey font-thin"
                                                            />
                                                        )}
                                                    </span>
                                                    <div className="mx-4 mt-6 flex max-w-3xl flex-wrap items-stretch justify-center gap-4">
                                                        <ExampleList onExampleClicked={onExampleClicked} />
                                                    </div>
                                                </div>
                                            </div>
                                        ) : !lastQuestionRef.current && windowWidth < 976 ? (
                                            <div className="relative h-full">
                                                <div className="flex flex-col h-full w-full items-center justify-center text-[36px] lg:text-[50px] font-thin leading-[56px]">
                                                    <p className="max-w-4xl text-center">
                                                        {" "}
                                                        <span className="text-eq-green">Ask Ellie</span> a question
                                                    </p>
                                                    <div className="mx-4 mt-6 flex max-w-3xl flex-wrap items-stretch justify-center gap-4">
                                                        <ExampleList onExampleClicked={onExampleClicked} />
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="relative h-full">
                                                <div className="h-full overflow-y-auto w-full" ref={chatScrollRef}>
                                                    <div className="p-0">
                                                        <div className="flex flex-col">
                                                            {isStreaming &&
                                                                streamedAnswers.map((streamedAnswer, index) => (
                                                                    <div key={index} className={"" + (index === 0 ? "" : "")}>
                                                                        <UserChatMessage
                                                                            message={streamedAnswer[0]}
                                                                            files={questionFiles[index]?.files || []}
                                                                            images={questionImages[index]?.images || []}
                                                                        />
                                                                        <Answer
                                                                            isStreaming={true}
                                                                            key={index}
                                                                            answer={streamedAnswer[1]}
                                                                            onCitationClicked={(filePath, citationTitle) =>
                                                                                onShowCitation(filePath, citationTitle, index)
                                                                            }
                                                                            onThoughtProcessClicked={() => onThoughtProcessClicked(index)}
                                                                            onCopyAnswerClicked={() =>
                                                                                copyToClipboard(streamedAnswer[1].choices[0].message.content)
                                                                            }
                                                                            showThoughts={showThoughts[index]}
                                                                            lastAnswer={index === answers.length}
                                                                            commentInput={commentInputs[index]}
                                                                            setCommentInput={commentInput => onSetCommentInput(commentInput, index)}
                                                                            showEmailDialog={showEmailDialog}
                                                                            setShowEmailDialog={b => setShowEmailDialog(b)}
                                                                            windowWidth={windowWidth}
                                                                            toolCalls={index === answers.length ? streamingToolCalls : undefined}
                                                                        />
                                                                    </div>
                                                                ))}
                                                            {!isStreaming &&
                                                                answers.map((answer, index) => (
                                                                    <div key={index} className={"" + (index === 0 ? "" : "")}>
                                                                        <UserChatMessage
                                                                            message={answer[0]}
                                                                            files={questionFiles[index]?.files || []}
                                                                            images={questionImages[index]?.images || []}
                                                                        />
                                                                        <Answer
                                                                            isStreaming={false}
                                                                            key={index}
                                                                            answer={answer[1]}
                                                                            onCitationClicked={(filePath, citationTitle) =>
                                                                                onShowCitation(filePath, citationTitle, index)
                                                                            }
                                                                            onThoughtProcessClicked={() => onThoughtProcessClicked(index)}
                                                                            onCopyAnswerClicked={() => copyToClipboard(answer[1].choices[0].message.content)}
                                                                            showThoughts={showThoughts[index]}
                                                                            lastAnswer={index === answers.length}
                                                                            commentInput={commentInputs[index]}
                                                                            setCommentInput={commentInput => onSetCommentInput(commentInput, index)}
                                                                            showEmailDialog={showEmailDialog}
                                                                            setShowEmailDialog={b => setShowEmailDialog(b)}
                                                                            windowWidth={windowWidth}
                                                                        />
                                                                    </div>
                                                                ))}
                                                            {isLoading && (
                                                                <div className={"" + (answers.length === 0 ? "" : "")}>
                                                                    <UserChatMessage
                                                                        message={lastQuestionRef.current}
                                                                        files={lastQuestionFilesRef.current}
                                                                        images={lastQuestionImagesRef.current}
                                                                    />
                                                                    <AnswerLoading activeTool={streamingToolCalls.at(-1) ?? null} />
                                                                </div>
                                                            )}
                                                            {error ? (
                                                                <div className={"" + (answers.length === 0 ? "" : "")}>
                                                                    <UserChatMessage
                                                                        message={lastQuestionRef.current}
                                                                        files={lastQuestionFilesRef.current}
                                                                        images={lastQuestionImagesRef.current}
                                                                    />
                                                                    <AnswerError
                                                                        error={error.toString()}
                                                                        onRetry={() => makeApiRequest(lastQuestionRef.current)}
                                                                    />
                                                                </div>
                                                            ) : null}
                                                            <div ref={chatMessageStreamEnd} />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                </div>
                                {appStateContext?.state.assistant && (
                                    <div className="w-full pt-0">
                                        <QuestionInput
                                            clearOnSend
                                            placeholder={"Message " + appStateContext?.state.assistant?.name}
                                            disabled={
                                                appStateContext.state.answerLoading ||
                                                appStateContext.state.files.some(file => file.status === "Loading") ||
                                                isGeneratingHistory
                                            }
                                            onSend={(question, images) => makeApiRequest(question, images)}
                                            fileInputRef={fileInputRef}
                                            uploadFile={uploadFile}
                                            showToEnd={!isAutoScroll && lastQuestionRef.current !== ""}
                                            toEnd={scrollToEnd}
                                            onStop={() => stopStreaming()}
                                            isStreaming={isStreaming}
                                            isLoading={isLoading}
                                        />
                                    </div>
                                )}
                                {appStateContext?.state.assistantsLoadingState === AssistantsLoadingState.Loading && (
                                    <div className="relative h-full">
                                        <div className="flex h-full flex-col items-center justify-center">
                                            <a className="gap-1 w-fit font-bold flex text-center items-center text-lg whitespace-nowrap">
                                                <span className="flex justify-center items-center h-[26.67px] w-[26.67px]">
                                                    <Spinner style={{ display: "block", verticalAlign: "middle" }} styles={spinnerStyles} />
                                                </span>
                                                <span className="text-[0.875rem]">
                                                    <span className="">AskEllie is loading...</span>
                                                </span>
                                            </a>
                                        </div>
                                    </div>
                                )}
                            </div>
                            {activeCitation && (
                                <div className="flex-1 overflow-y-auto ml-6 mt-[18px] mb-[32px] mr-16 hidden w-1/2 xl:flex">
                                    <div className="w-full px-0 justify-center md:gap-6 m-auto">
                                        <div className="relative flex w-full flex-col">
                                            <div className="flex justify-between">
                                                <div className="flex grow justify-start max-w-[calc(100%-4rem)]">
                                                    <div className="leading-7 font-semibold text-[0.875rem] mt-0.5 mb-2.5 text-ellipsis overflow-x-hidden whitespace-nowrap">
                                                        {activeCitationTitle}
                                                    </div>
                                                </div>
                                                <div className="flex justify-end">
                                                    <ButtonTooltip title="Hide source" placement="top">
                                                        <span className="">
                                                            <a className="h-10 rounded-lg px-1 text-eq-grey cursor-pointer" onClick={onCloseCitation}>
                                                                <span className="h-[24px] w-[24px] shrink-0 text-2xl material-symbols-outlined">
                                                                    visibility_off
                                                                </span>
                                                            </a>
                                                        </span>
                                                    </ButtonTooltip>
                                                </div>
                                            </div>
                                            <iframe key={iframeKey} title="Citation" src={activeCitation} width="100%" height={windowHeight - 236 + "px"} />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </main>
                    </div>
                    {windowWidth < 976 && (
                        <MobileSidebar
                            onNewClick={clearChat}
                            newDisabled={!lastQuestionRef.current || appStateContext?.state.answerLoading}
                            onHistoryClick={handleHistoryClick}
                        />
                    )}
                </div>
            </ClearChatProvider>
        </>
    );
};

export default Chat;
